/* =============================================================================
   WindowStackService + KeyService - the ONE window stack and the ONE key
   dispatcher of the library.

   WindowStackService owns the LIFO: every window, picker and popup push()es on
   open and pop()s on EVERY close path - the header close chip, a programmatic
   close and destroy alike. A close path that does not pop leaves a stale entry,
   and the next Escape then targets a window that is already gone.

   KeyService owns the document listener and the ORDERING. Components ship NO
   private global key handlers: they register() here and unregister on destroy.

   Focus-restore on close rides on the release() returned by
   A11yService.trapFocus (services/a11y.js).
   ============================================================================= */
import { LayoutService } from '../components/ics-layout.js';   /* LayoutService (the z-index scale) lives in the unified layout module; the import is side-effect free - that module registers nothing at import */

/* ONE STACK PER REALM, NOT PER BUNDLE. A page shell loads several esbuild
   bundles and each compiles its OWN copy of this module. Any bundle that reaches
   `components/ics-window.js` (the dialogs entry; the tree entry, whose picker builds
   a Window) would otherwise carry a private `_stack`: a window MOUNTED by one
   copy would push onto that copy's stack, while `$.fn.icsWindow('close')` -
   attached by the dialogs copy - would search the dialogs copy's stack, find
   nothing, fall through to close-topmost, find nothing there either, and return
   silently. No throw, no console line: a Close button that does nothing.
   So the state lives on the realm under a Symbol.for key - the same device
   `Component.KEY` uses for the instance key - and every copy of this module, in
   every bundle the page loads, pushes to and pops from the SAME LIFO; the
   one-ESC-one-window guard and the key-handler list are likewise one. */
const _shared = (() => {
  const K = Symbol.for('ics.windowstack');
  const g = globalThis;
  if (!g[K]) g[K] = { stack: [], escActive: false, escConsumed: false, escProbe: false, keyHandlers: [], keyBound: false };
  return g[K];
})();
const _stack = _shared.stack;   // entries: {id, close():void, el[, box:true]}
/* `box: true` marks a message box or toast: it sits on the stack for z-order and
   Escape-yield, but it is NOT a close-topmost target - icsWindow('close'),
   IcsWindowClose and the central Escape dispatch all walk PAST it to the topmost
   real window. A box is answered only through its own buttons and keys, so a
   picker callback that opens a confirmation and then closes its own window closes
   the picker and leaves the question standing. */

/* ---- the ONE-CLOSE-PER-ESC guard -------------------------------------------
   Several handlers can see the SAME Escape keypress: the central KeyService
   dispatcher, a message box's own capture-phase handler, and any page-level
   `$(document).keydown(... icsWindow('close'))` handler a screen installs for
   itself. Each of them routes into closeTop(), so without a guard ONE keypress
   would close two or three stacked windows. A capture-phase probe marks an
   Escape dispatch as "in flight"; the FIRST close consumes it and every later
   close in the same dispatch is a no-op. The contract that buys: one ESC = one
   window. Non-Escape closes are never guarded. */
/* the three flags live on `_shared` (realm-wide): a probe installed by one bundle
   copy must be the probe every copy consults, or two copies would each install
   one and each answer the guard for itself */
function _installEscProbe() {
  if (_shared.escProbe || typeof document === 'undefined') return;
  _shared.escProbe = true;
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    _shared.escActive = true;
    _shared.escConsumed = false;
    setTimeout(() => { _shared.escActive = false; _shared.escConsumed = false; }, 0);
  }, true);                                                // CAPTURE: ahead of every other handler
}

export const WindowStackService = {
  /** push on open; returns the z assigned from the monotonic window counter */
  push(entry) {
    _installEscProbe();
    _stack.push(entry);
    return LayoutService.nextWindowZ();
  },

  /** pop a SPECIFIC entry - the close chip, a direct close and destroy all route
      here. Searches from the TOP, so when the same id was pushed twice the LAST
      occurrence is the one removed. */
  pop(id) {
    for (let i = _stack.length - 1; i >= 0; i--) {
      if (_stack[i].id === id) { _stack.splice(i, 1); return; }
    }
  },

  top() { return _stack[_stack.length - 1] || null; },
  get depth() { return _stack.length; },

  /** the topmost entry that a close-topmost may target (boxes skipped), or null */
  topClosable() {
    for (let i = _stack.length - 1; i >= 0; i--) { if (!_stack[i].box) return _stack[i]; }
    return null;
  },
  /** how many close-topmost targets are open (boxes not counted) */
  get closableDepth() { let n = 0; for (const e of _stack) { if (!e.box) n++; } return n; },

  /** mark the in-flight Escape as already handled - a box calls this after
      closing itself, so the central dispatcher stands down for that keypress */
  noteEscConsumed() { if (_shared.escActive) _shared.escConsumed = true; },

  /** true when an Escape dispatch already closed something this tick */
  escBlocked() { return _shared.escActive && _shared.escConsumed; },

  /** ESC contract: close the TOPMOST only, never close-all.
      Boxes are walked past (see the entry note above). Called from the key
      dispatcher, so it declares Esc origin. */
  closeTop(opts) { return this.closeTopOf(null, opts || { esc: true }); },

  /** close the topmost entry that satisfies `pred(entry)` (boxes always skipped;
      null pred = any). This is how icsWindow('close') honours a NAMED receiver:
      pred = "entry.el is one of the receiver elements".
      `opts.esc` declares the ORIGIN of the close and defaults to FALSE:
      ⚠ only an Esc-origin close consults and arms the one-ESC-one-window guard.
      A PROGRAMMATIC close running synchronously inside an Escape dispatch (a
      messager `onNo`/`fn(false)`, or a window's onClose cascading a child close
      - Esc ≡ No is the documented contract) is never blocked and never consumes
      the keypress. Were the guard applied to every caller, such a close would be
      silently refused and the ambiguous `false` would send icsWindow('close')
      into its close-topmost fallback, closing the WRONG window. Returns true
      when a window was closed. */
  closeTopOf(pred, opts) {
    const fromEsc = !!(opts && opts.esc);
    if (fromEsc && this.escBlocked()) return false;        // one ESC = one window
    for (let i = _stack.length - 1; i >= 0; i--) {
      const e = _stack[i];
      if (e.box) continue;
      if (typeof pred === 'function' && !pred(e)) continue;
      if (fromEsc) this.noteEscConsumed();
      this.pop(e.id);
      try { e.close(); } catch (err) { console.error('[ics] window close failed', err); }
      return true;
    }
    return false;
  },

  /** test seam - drains the stack (never used by app code) */
  _reset() { _stack.length = 0; _shared.escActive = false; _shared.escConsumed = false; },
};

/* ================================================================================
   KeyService - THE ONE CENTRAL KEY DISPATCHER
   ================================================================================
   ONE document listener, ONE decision, TOPMOST target only. The alternative -
   each screen and each component binding its own document-level key handler -
   means a single Escape fires all of them and closes three stacked windows at
   once; that is the failure this service exists to make impossible.

   THE CONTRACT
   - ESC closes the TOPMOST entry only. One ESC = one window.
   - **Esc ≡ No / dismiss** on the topmost dialog: the entry's own `close()` runs,
     so a confirmation's `onNo` / `fn(false)` fires - Esc is an ANSWER, not an
     abort, and a caller must not treat it as "nothing happened".
   - Components ship NO private global key handlers. A component that needs keys
     `register()`s here and unregisters on destroy - nothing leaks, nothing
     double-fires.
   - Enter and the arrows route the same way (topmost first). That is what lets a
     grid's Enter->next-cell and a picker's arrow walk coexist without either one
     installing its own document listener.
   - A handler that throws is logged and skipped: one broken handler can never
     disable Escape for the whole page.

   INSTALL IS PAGE-SCOPED BY DESIGN: only a shell that owns all key handling calls
   install(). On a page that still carries its own document-level Escape handlers,
   adding this dispatcher on top would close TWO windows per press - such a page
   keeps its existing behaviour and does not install.

   SPLIT OF DUTIES: WindowStackService owns the stack and the ONE-CLOSE-PER-ESC
   guard, which must work on pages where this dispatcher is NOT installed;
   KeyService owns the LISTENER and the ORDERING.
   ================================================================================ */

/** the keys this service dispatches. Deliberately closed: it must never become
    a bucket where arbitrary app shortcuts accumulate. */
const DISPATCHED_KEYS = Object.freeze(['Escape', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/** numeric `which` codes, filled in for handlers that read the code instead of e.key */
const WHICH = { Escape: 27, Enter: 13, ArrowLeft: 37, ArrowUp: 38, ArrowRight: 39, ArrowDown: 40 };

const _keyHandlers = _shared.keyHandlers;   // realm-wide: a handler registered through any bundle copy is consulted by THE dispatcher

export const KeyService = {
  /* the install latch is realm-wide too: the second bundle to call install()
     must find the listener already bound, or one Escape would dispatch twice */
  get _bound() { return _shared.keyBound; },
  set _bound(v) { _shared.keyBound = !!v; },

  KEYS: DISPATCHED_KEYS,

  /**
   * Register a key handler. Consulted TOPMOST-FIRST (last registered wins); the
   * first handler to return `true` CONSUMES the event and stops the walk.
   * @param {Function} fn      (ev, key) => truthy to consume
   * @param {object}   [opts]  {keys:[...]} to narrow; omit = every dispatched key
   * @returns {Function} unregister - call it from destroy()
   */
  register(fn, opts) {
    if (typeof fn !== 'function') return () => {};
    const rec = { fn, keys: (opts && opts.keys) || null };
    _keyHandlers.push(rec);
    return () => {
      const i = _keyHandlers.indexOf(rec);
      if (i >= 0) _keyHandlers.splice(i, 1);
    };
  },

  /** live handler count - a non-zero count after teardown means a handler leaked */
  count() { return _keyHandlers.length; },

  /**
   * THE dispatch. Public so a test can drive it directly and a host page can
   * forward a key out of an iframe.
   * @returns {boolean} whether the event was consumed
   */
  dispatch(e) {
    if (!e || DISPATCHED_KEYS.indexOf(e.key) < 0) return false;
    if (e.which === undefined && WHICH[e.key] !== undefined) {
      try { Object.defineProperty(e, 'which', { value: WHICH[e.key], configurable: true }); } catch (err) { /* frozen */ }
    }
    for (let i = _keyHandlers.length - 1; i >= 0; i--) {     // TOPMOST first
      const rec = _keyHandlers[i];
      if (rec.keys && rec.keys.indexOf(e.key) < 0) continue;
      let consumed = false;
      try {
        consumed = rec.fn(e, e.key);
      } catch (err) {
        /* one broken handler must NEVER disable ESC for the whole app */
        console.error('[ics] KeyService: handler threw; continuing', err);
      }
      if (consumed === true) return true;
    }
    if (e.key === 'Escape' && WindowStackService.closableDepth && !WindowStackService.escBlocked()) {   // a toast alone is not an Escape target
      if (typeof e.preventDefault === 'function') e.preventDefault();
      WindowStackService.closeTop();                        // Esc ≡ No: the entry's own close() runs
      return true;
    }
    return false;
  },

  /** ONE document-level dispatcher; the shell calls this once, and a second call
      is a no-op. CAPTURE phase, so the dispatcher sees the key before any
      page-level handler that bubbles - which is what makes "topmost only"
      actually enforceable rather than merely intended. */
  install(binder) {
    if (this._bound) return;
    this._bound = true;
    _installEscProbe();
    binder.add(document, 'keydown', (e) => { KeyService.dispatch(e); }, true);
  },

  isInstalled() { return this._bound; },

  /** test seam - drains handlers and clears the install latch (never used by app code) */
  _reset() { _keyHandlers.length = 0; this._bound = false; },
};
